import type { LiteSecodaEntity, LiteSecodaEntityOut } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import { has } from 'lodash-es';
import type {
	IBaseModel,
	ICollection,
	IDocument,
	IGlossaryTerm,
	IMetric,
	ISecodaEntity,
	IUser,
	IUserGroup,
} from '../../api';
import type { BreadcrumbEntity } from '../../utils/breadcrumb';

export function getEntityDisplayTitle(entity?: {
	title?: string | null;
	title_cased?: string | null;
}): string {
	return entity?.title_cased || entity?.title || 'Untitled';
}

export function isUser(item: IBaseModel): item is IUser {
	return has(item, 'display_name');
}

export function isUserGroup(item: IBaseModel): item is IUserGroup {
	return has(item, 'name');
}

/**
 * Type guard for checking if an entity is a Metric.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Metric, `false` otherwise.
 */
export function isMetric(
	entity: BreadcrumbEntity | LiteSecodaEntity
): entity is IMetric {
	return entity.entity_type === EntityType.metric;
}

/**
 * Type guard for checking if an entity is a Table.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Table, `false` otherwise.
 */
export function isTable(entity: BreadcrumbEntity | LiteSecodaEntity): boolean {
	return entity.entity_type === EntityType.table;
}

/**
 * Type guard for checking if an entity is a Column.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Column, `false` otherwise.
 */
export function isColumn(entity: BreadcrumbEntity | LiteSecodaEntity): boolean {
	return entity.entity_type === EntityType.column;
}

/**
 * Type guard for checking if an entity is a Dashboard.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Dashboard, `false` otherwise.
 */
export function isDashboard(
	entity: BreadcrumbEntity | LiteSecodaEntity
): boolean {
	return entity.entity_type === EntityType.dashboard;
}

/**
 * Type guard for checking if an entity is a Chart.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Chart, `false` otherwise.
 */
export function isChart(entity: BreadcrumbEntity | LiteSecodaEntity): boolean {
	return entity.entity_type === EntityType.chart;
}

/**
 * Type guard for checking if an entity is a Job.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Job, `false` otherwise.
 */
export function isJob(entity: BreadcrumbEntity | LiteSecodaEntity): boolean {
	return entity.entity_type === EntityType.job;
}

/**
 * Type guard for checking if an entity is an Event.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is an Event, `false` otherwise.
 */
export function isEvent(entity: BreadcrumbEntity | LiteSecodaEntity): boolean {
	return entity.entity_type === EntityType.event;
}

/**
 * Type guard for checking if an entity is a Schema.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Schema, `false` otherwise.
 */
export function isSchema(entity: BreadcrumbEntity | LiteSecodaEntity): boolean {
	return entity.entity_type === EntityType.schema;
}

/**
 * Type guard for checking if an entity is a Database.
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Database, `false` otherwise.
 */
export function isDatabase(
	entity: BreadcrumbEntity | LiteSecodaEntity
): boolean {
	return entity.entity_type === EntityType.database;
}

/**
 * Type guard for checking if an entity is a Glossary.
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Glossary, `false` otherwise.
 */
export function isGlossary(
	entity: BreadcrumbEntity | LiteSecodaEntity
): entity is IGlossaryTerm {
	return entity.entity_type === EntityType.glossary;
}

/**
 * Type guard for checking if an entity is a Collection.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Collection, `false` otherwise.
 */
export function isCollection(
	entity: BreadcrumbEntity | LiteSecodaEntity
): entity is ICollection {
	return entity.entity_type === EntityType.collection;
}

/**
 * Type guard for checking if an entity is a Document.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Document, `false` otherwise.
 */
export function isDocument(
	entity: BreadcrumbEntity | LiteSecodaEntity
): entity is IDocument {
	return entity.entity_type === EntityType.document;
}

/**
 * Type guard for checking if an entity is a Question.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Question, `false` otherwise.
 */
export function isQuestion(
	entity: BreadcrumbEntity | LiteSecodaEntity
): entity is IDocument {
	return entity.entity_type === EntityType.question;
}

/**
 * Type guard for checking if an entity is a BI Dashboard.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a BI Dashboard, `false` otherwise.
 */
export function isBIDashboard(entity: BreadcrumbEntity | LiteSecodaEntity) {
	return entity.entity_type === EntityType.bi_dashboard;
}

/**
 * Type guard for checking if an entity is a BI Metric.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a BI Metric, `false` otherwise.
 */
export function isBIMetric(entity: BreadcrumbEntity | LiteSecodaEntity) {
	return entity.entity_type === EntityType.bi_metric;
}

/**
 * Type guard for checking if an entity is a Generic Group.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Generic Group, `false` otherwise.
 */
export function isGenericGroup(
	entity: BreadcrumbEntity | LiteSecodaEntity
): boolean {
	return entity.entity_type === EntityType.generic_group;
}

/**
 * Type guard for checking if an entity is a Generic Entity.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Generic Entity, `false` otherwise.
 */
export function isGeneric(
	entity: BreadcrumbEntity | LiteSecodaEntity
): boolean {
	return entity.entity_type === EntityType.generic;
}

/**
 * Type guard for checking if an entity is a Catalog Entity.
 *
 * @param entity - The entity to check.
 * @returns `true` if the entity is a Table, `false` otherwise.
 */
export function isCatalogEntity(
	entity: BreadcrumbEntity | LiteSecodaEntity
): boolean {
	return (
		isDatabase(entity) ||
		isSchema(entity) ||
		isTable(entity) ||
		isColumn(entity) ||
		isDashboard(entity) ||
		isChart(entity) ||
		isEvent(entity) ||
		isJob(entity) ||
		isGenericGroup(entity) ||
		isGeneric(entity)
	);
}

export function getEntityPathString(
	value: LiteSecodaEntityOut | ISecodaEntity
): string {
	const entityType = value.entity_type;
	const nativeType = value.native_type;
	const searchMetadata = value.search_metadata || {};

	// Use native_type if available, otherwise fall back to entity_type
	const type = (nativeType || entityType || '')
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (l) => l.toUpperCase());
	let metadata: string | null = null;

	if (entityType === 'table') {
		if (searchMetadata.database && searchMetadata.schema) {
			metadata = `${searchMetadata.database} / ${searchMetadata.schema}`;
		}
	} else if (entityType === 'column') {
		if (
			searchMetadata.database &&
			searchMetadata.schema &&
			searchMetadata.table
		) {
			metadata = `${searchMetadata.database} / ${searchMetadata.schema} / ${searchMetadata.table}`;
		}
	} else if (entityType === 'dashboard') {
		if (searchMetadata.product && searchMetadata.group) {
			metadata = `${searchMetadata.product} / ${searchMetadata.group}`;
		}
	} else if (['chart', 'job'].includes(entityType || '')) {
		if (searchMetadata.group) {
			metadata = searchMetadata.group;
		}
	}

	return metadata ? `${type} · ${metadata}` : type;
}
